import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Vaga } from "@/types/vaga";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Plus, Calendar, TrendingUp, AlertTriangle, Building2, DollarSign } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAgencias, useAgenciasInvalidate } from "@/hooks/use-agencias";
import { reportUserError } from "@/lib/report-error";
import { formatLocalDate, logicalDateToTimestamp, timestampToLocalDate } from "@/utils/dateUtils";
import { formatCents, MOEDAS, parseMoeda, simboloMoeda } from "@/utils/money";

interface VagaFormProps {
  novaVaga: Partial<Vaga>;
  setNovaVaga: (vaga: Partial<Vaga>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

// Radix Select não aceita SelectItem com value="" — usa esse sentinel pra
// oferecer "voltar pro vazio" nos combos que hoje não têm como limpar.
const LIMPAR_SELECAO = "__limpar__";

export function VagaForm({
  novaVaga,
  setNovaVaga,
  onSubmit
}: VagaFormProps) {
  const [areas, setAreas] = useState<{
    id: string;
    nome: string;
  }[]>([]);
  // Agências vêm do hook com cache compartilhado (queryKey ["agencias"]) — antes
  // era um fetch cru aqui, disparando uma 2ª query idêntica ao abrir o diálogo.
  const { data: agencias = [] } = useAgencias();
  const invalidateAgencias = useAgenciasInvalidate();
  const agenciaDaVaga = agencias.find((agencia) => agencia.id === novaVaga.agencia_id);
  // Sem escolha na vaga, vale o padrão da agência (o banco preenche ao criar).
  const moedaDaVaga = parseMoeda(novaVaga.moeda ?? agenciaDaVaga?.moeda_padrao);
  const [salvandoPadrao, setSalvandoPadrao] = useState(false);
  const [loading, setLoading] = useState(false);
  const today = formatLocalDate(new Date()) ?? "";
  const [isNovaAreaDialogOpen, setIsNovaAreaDialogOpen] = useState(false);
  const [isNovaAgenciaDialogOpen, setIsNovaAgenciaDialogOpen] = useState(false);
  const [novaAreaNome, setNovaAreaNome] = useState("");
  const [novaAgenciaNome, setNovaAgenciaNome] = useState("");
  const [criandoArea, setCriandoArea] = useState(false);
  const [criandoAgencia, setCriandoAgencia] = useState(false);
  const [verbaMinima, setVerbaMinima] = useState("");
  const [verbaMaxima, setVerbaMaxima] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchAreas();
  }, []);

  // Sync initial values from novaVaga
  useEffect(() => {
    if (novaVaga.verba_minima !== undefined) {
      setVerbaMinima(novaVaga.verba_minima ? formatCurrencyDisplay(novaVaga.verba_minima) : "");
    }
    if (novaVaga.verba_maxima !== undefined) {
      setVerbaMaxima(novaVaga.verba_maxima ? formatCurrencyDisplay(novaVaga.verba_maxima) : "");
    }
  }, [novaVaga.verba_minima, novaVaga.verba_maxima, moedaDaVaga]);

  const fetchAreas = async () => {
   try {
      const {
        data,
        error
      } = await supabase.from("areas_atuacao").select("*").eq("is_active", true).order("nome");
      if (error) throw error;
      setAreas(data);
    } catch (error) {
      console.error("Erro ao carregar áreas:", error);
    }
  };

  const handleCriarNovaArea = async () => {
    if (!novaAreaNome.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite o nome da área de atuação.",
        variant: "destructive",
      });
      return;
    }

    setCriandoArea(true);
    try {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .insert([{ nome: novaAreaNome.trim() }])
        .select()
        .single();

      if (error) throw error;

      // Atualiza a lista de áreas
      await fetchAreas();
      
      // Seleciona automaticamente a nova área criada
      setNovaVaga({
        ...novaVaga,
        area_id: data.id,
        areas: data.nome
      });

      // Limpa o formulário e fecha o dialog
      setNovaAreaNome("");
      setIsNovaAreaDialogOpen(false);

      toast({
        title: "Sucesso",
        description: "Nova área de atuação criada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao criar área:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar a área de atuação. " + reportUserError(error, { source: "vaga-form" }).description,
        variant: "destructive",
      });
    } finally {
      setCriandoArea(false);
    }
  };

  const handleCriarNovaAgencia = async () => {
    if (!novaAgenciaNome.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite o nome da agência.",
        variant: "destructive",
      });
      return;
    }

    setCriandoAgencia(true);
    try {
      const { data, error } = await supabase
        .from("agencias")
        .insert([{ nome: novaAgenciaNome.trim() }])
        .select()
        .single();

      if (error) throw error;

      // Atualiza a lista de agências (cache compartilhado)
      invalidateAgencias();

      // Seleciona automaticamente a nova agência criada
      setNovaVaga({
        ...novaVaga,
        agencia_id: data.id,
        agencia: data.nome
      });

      // Limpa o formulário e fecha o dialog
      setNovaAgenciaNome("");
      setIsNovaAgenciaDialogOpen(false);

      toast({
        title: "Sucesso",
        description: "Nova agência criada com sucesso!",
      });
    } catch (error) {
      console.error("Erro ao criar agência:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar a agência. " + reportUserError(error, { source: "vaga-form" }).description,
        variant: "destructive",
      });
    } finally {
      setCriandoAgencia(false);
    }
  };

  const handleAreaChange = async (areaId: string) => {
    setNovaVaga({
      ...novaVaga,
      area_id: areaId,
      areas: areaId
    });
  };

  const handleAgenciaChange = (agenciaId: string) => {
    const selectedAgencia = agencias.find(agencia => agencia.id === agenciaId);
    setNovaVaga({
      ...novaVaga,
      agencia_id: agenciaId,
      agencia: selectedAgencia?.nome || ""
    });
  };

  // Utility functions for currency formatting
  const formatCurrencyDisplay = (centavos: number): string => {
    return formatCents(centavos, moedaDaVaga);
  };

  const formatCurrencyInput = (value: string): string => {
    // Remove tudo exceto dígitos
    const digits = value.replace(/\D/g, '');
    
    if (!digits) return '';
    
    // Converte para número e divide por 100 para ter centavos
    const number = parseInt(digits) / 100;
    
    return formatCents(Math.round(number * 100), moedaDaVaga);
  };

  const parseCurrencyToCentavos = (value: string): number => {
    // Remove tudo exceto dígitos
    const digits = value.replace(/\D/g, '');
    return digits ? parseInt(digits) : 0;
  };

  const handleVerbaChange = (field: 'verba_minima' | 'verba_maxima', value: string) => {
    const formatted = formatCurrencyInput(value);
    const centavos = parseCurrencyToCentavos(value);
    
    if (field === 'verba_minima') {
      setVerbaMinima(formatted);
    } else {
      setVerbaMaxima(formatted);
    }
    
    setNovaVaga({
      ...novaVaga,
      [field]: centavos || null
    });
  };

  const usarComoPadraoDaAgencia = async () => {
    if (!agenciaDaVaga) return;
    setSalvandoPadrao(true);
    try {
      const { error } = await supabase.from("agencias").update({ moeda_padrao: moedaDaVaga }).eq("id", agenciaDaVaga.id);
      if (error) throw error;
      invalidateAgencias();
    } catch (error) {
      reportUserError(error, { action: "mudar o padrão da agência", source: "vaga-form" });
    } finally {
      setSalvandoPadrao(false);
    }
  };

  const isEditing = !!novaVaga.id;

  return <form onSubmit={e => {
    setLoading(true);
    try {
      onSubmit(e);
    } finally {
      // Mesmo em caso de erro, liberamos o estado de loading após 1 segundo
      // para não travar a interface
      setTimeout(() => setLoading(false), 1000);
    }
  }} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="agencia" className="text-sm font-medium flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Agência
        </Label>
        <div className="flex gap-2">
          <Select value={novaVaga.agencia_id || ""} onValueChange={handleAgenciaChange}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Selecione a agência" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              {agencias.map(agencia => (
                <SelectItem key={agencia.id} value={agencia.id}>
                  {agencia.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={isNovaAgenciaDialogOpen} onOpenChange={setIsNovaAgenciaDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Criar Nova Agência</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nova-agencia-nome">Nome da Agência</Label>
                  <Input
                    id="nova-agencia-nome"
                    value={novaAgenciaNome}
                    onChange={(e) => setNovaAgenciaNome(e.target.value)}
                    placeholder="Digite o nome da nova agência"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCriarNovaAgencia();
                      }
                    }}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setIsNovaAgenciaDialogOpen(false);
                      setNovaAgenciaNome("");
                    }}
                    disabled={criandoAgencia}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleCriarNovaAgencia}
                    disabled={criandoAgencia}
                  >
                    {criandoAgencia ? "Criando..." : "Criar Agência"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cliente" className="text-sm font-medium">
          Cliente
        </Label>
        <Input id="cliente" value={novaVaga.cliente || ""} onChange={e => setNovaVaga({
        ...novaVaga,
        cliente: e.target.value
      })} placeholder="Nome do cliente" required />
      </div>

      <div className="space-y-2">
        <Label htmlFor="area" className="text-sm font-medium">
          Área de Atuação
        </Label>
        <div className="flex gap-2">
          <Select value={novaVaga.areas || ""} onValueChange={handleAreaChange}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Selecione a área" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              {areas.map(area => <SelectItem key={area.id} value={area.nome}>
                  {area.nome}
                </SelectItem>)}
            </SelectContent>
          </Select>
          
          <Dialog open={isNovaAreaDialogOpen} onOpenChange={setIsNovaAreaDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Criar Nova Área de Atuação</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nova-area-nome">Nome da Área</Label>
                  <Input
                    id="nova-area-nome"
                    value={novaAreaNome}
                    onChange={(e) => setNovaAreaNome(e.target.value)}
                    placeholder="Digite o nome da nova área"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCriarNovaArea();
                      }
                    }}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setIsNovaAreaDialogOpen(false);
                      setNovaAreaNome("");
                    }}
                    disabled={criandoArea}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleCriarNovaArea}
                    disabled={criandoArea}
                  >
                    {criandoArea ? "Criando..." : "Criar Área"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="descricao_curta" className="text-sm font-medium">
          Descrição curta
        </Label>
        <Input id="descricao_curta" value={novaVaga.descricao_curta || ""} maxLength={120} onChange={e => setNovaVaga({
        ...novaVaga,
        descricao_curta: e.target.value
      })} placeholder='Pra diferenciar de vagas parecidas (ex.: "squad de social")' className="dark:border-gray-700 dark:bg-gray-800" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="descricao" className="text-sm font-medium">
          Descrição
        </Label>
        <Textarea id="descricao" value={novaVaga.descricao || ""} onChange={e => setNovaVaga({
        ...novaVaga,
        descricao: e.target.value
      })} placeholder="Descrição da vaga" className="dark:border-gray-700 dark:bg-gray-800" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="requisitos" className="text-sm font-medium">
          Requisitos
        </Label>
        <Textarea id="requisitos" value={novaVaga.requisitos || ""} onChange={e => setNovaVaga({
        ...novaVaga,
        requisitos: e.target.value
      })} placeholder="Requisitos para a vaga" className="dark:border-gray-700 dark:bg-gray-800" />
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="verba" className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Faixa de Verba
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="moeda" className="text-xs text-muted-foreground">
                Moeda
              </Label>
              <Select value={moedaDaVaga} onValueChange={(moeda) => setNovaVaga({ ...novaVaga, moeda })}>
                <SelectTrigger id="moeda">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOEDAS.map((opcao) => (
                    <SelectItem key={opcao.value} value={opcao.value}>
                      {opcao.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {agenciaDaVaga && parseMoeda(agenciaDaVaga.moeda_padrao) !== moedaDaVaga && (
                <button
                  type="button"
                  disabled={salvandoPadrao}
                  className="text-left text-xs font-medium text-primary hover:underline disabled:opacity-50"
                  onClick={() => void usarComoPadraoDaAgencia()}
                >
                  Usar {simboloMoeda(moedaDaVaga)} como padrão de {agenciaDaVaga.nome}
                </button>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="verba_minima" className="text-xs text-muted-foreground">
                Verba Mínima
              </Label>
              <Input 
                id="verba_minima" 
                value={verbaMinima}
                onChange={e => handleVerbaChange('verba_minima', e.target.value)}
                placeholder={`${simboloMoeda(moedaDaVaga)} 0,00`} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="verba_maxima" className="text-xs text-muted-foreground">
                Verba Máxima
              </Label>
              <Input 
                id="verba_maxima" 
                value={verbaMaxima}
                onChange={e => handleVerbaChange('verba_maxima', e.target.value)}
                placeholder={`${simboloMoeda(moedaDaVaga)} 0,00`} 
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="duracao_dias" className="text-sm font-medium">
            Duração (em dias)
          </Label>
          <Input 
            id="duracao_dias" 
            type="number" 
            min="1"
            value={novaVaga.duracao_dias || ""} 
            onChange={e => setNovaVaga({
              ...novaVaga,
              duracao_dias: e.target.value ? parseInt(e.target.value) : null
            })} 
            placeholder="Ex: 30, 60, 90" 
          />
        </div>
      </div>

      {novaVaga.status === "aprovado" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="data_aprovacao" className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Data de aprovação
            </Label>
            <Input
              id="data_aprovacao"
              type="date"
              max={today}
              value={timestampToLocalDate(novaVaga.data_aprovacao)}
              onChange={e => {
                const date = e.target.value;
                if (!date || date > today) return;
                setNovaVaga({
                  ...novaVaga,
                  data_aprovacao: logicalDateToTimestamp(date)
                });
              }}
            />
            <p className="text-xs text-muted-foreground">Define em que mês a vaga conta como aprovada.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="deadline_preenchimento" className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Deadline para Preenchimento
          </Label>
          <Input 
            id="deadline_preenchimento" 
            type="date"
            value={novaVaga.deadline_preenchimento || ""} 
            onChange={e => setNovaVaga({
              ...novaVaga,
              deadline_preenchimento: e.target.value
            })} 
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="complexidade" className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Complexidade (1-10)
          </Label>
          <Select 
            value={novaVaga.complexidade?.toString() || ""} 
            onValueChange={value => setNovaVaga({
              ...novaVaga,
              complexidade: value ? parseInt(value) : null
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a complexidade" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <SelectItem key={num} value={num.toString()}>
                  {num} - {num <= 3 ? 'Baixa' : num <= 6 ? 'Média' : num <= 8 ? 'Alta' : 'Muito Alta'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="urgencia" className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Urgência (1-3)
          </Label>
          <Select 
            value={novaVaga.urgencia?.toString() || ""} 
            onValueChange={value => setNovaVaga({
              ...novaVaga,
              urgencia: value ? parseInt(value) : null
            })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a urgência" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              <SelectItem value="1">1 - Baixa</SelectItem>
              <SelectItem value="2">2 - Média</SelectItem>
              <SelectItem value="3">3 - Alta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="formato" className="text-sm font-medium">
            Formato
          </Label>
          <Select value={novaVaga.formato || ""} onValueChange={value => setNovaVaga({
          ...novaVaga,
          formato: value === LIMPAR_SELECAO ? null : value
        })}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o formato" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              <SelectItem value={LIMPAR_SELECAO} className="text-muted-foreground">Nenhum</SelectItem>
              <SelectItem value="presencial">Presencial</SelectItem>
              <SelectItem value="hibrido">Híbrido</SelectItem>
              <SelectItem value="remoto">Remoto</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tipo_contrato" className="text-sm font-medium">
            Tipo de Contrato
          </Label>
          <Select value={novaVaga.tipo_contrato || ""} onValueChange={value => setNovaVaga({
          ...novaVaga,
          tipo_contrato: value === LIMPAR_SELECAO ? null : value
        })}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo de contrato" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              <SelectItem value={LIMPAR_SELECAO} className="text-muted-foreground">Nenhum</SelectItem>
              <SelectItem value="clt">CLT</SelectItem>
              <SelectItem value="pj">PJ</SelectItem>
              <SelectItem value="temporario">Temporário</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="modalidade" className="text-sm font-medium">
            Modalidade
          </Label>
          <Select value={novaVaga.modalidade || ""} onValueChange={value => setNovaVaga({
          ...novaVaga,
          modalidade: value === LIMPAR_SELECAO ? null : value as Vaga["modalidade"]
        })}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione a modalidade" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1A1F2C] dark:border-gray-700">
              <SelectItem value={LIMPAR_SELECAO} className="text-muted-foreground">Nenhum</SelectItem>
              <SelectItem value="frila">Frila</SelectItem>
              <SelectItem value="fixa">Fixa</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Salvando..." : isEditing ? "Atualizar Vaga" : "Criar Vaga"}
      </Button>
    </form>;
}
